/*
  Delivery promise on the product page (PDP-DELIVERY-DATE-1, 2026-10-29) – snippets/delivery-promise.liquid.

  1. Arm: treatment when sha256(salt + ":" + clientId), first 8 bytes big-endian, mod 100 < treatmentPct.
     clientId = Shopify's id as the theme reads it (ShopifyAnalytics.lib.user().traits().uniqToken = the web pixel's
     cid, as in rec-row.js). No analytics consent ⇒ Shopify gives the all-zero id ⇒ nothing is shown while
     treatmentPct < 100 (such a visitor cannot be measured); treatmentPct 100 shows it to everyone.
     The formula is the rec holdout's (numeric in SQL), so the arm of every visitor can be recomputed in timi-pg.
  2. GET <endpoint>?country=XX (timi-shipping /delivery-promise): {now, slots: [{order_before, ship, min, max}]}.
     Cached per tab in sessionStorage until the first cut-off passes. 3 s timeout. Any error / empty slots ⇒ nothing.
  3. Text: the first slot whose cut-off is still ahead. Under 24 h left ⇒ «Order within 2 h 15 min – delivered
     Tue 3 Nov», else «Order now – delivered Wed 4 Nov» (a 70 h countdown on a Friday afternoon reads oddly).
     The date is slot.max by default (block setting date_mode), formatted in Stockholm time with Intl parts in the
     page's language. Refreshed every 30 s; the server's «now» corrects a wrong device clock.
  4. Variant change (theme.js «variant:change»): shown only for a variant in stock (inStock map from Liquid).
  5. Preview themes only (Shopify.theme.role !== 'main'): ?dp_preview=treatment|control forces the arm and the
     switch, so the line can be tested on a duplicate before the shop metafield exists.
*/
(function () {
  'use strict';

  var CACHE_KEY = 'timi_dp_v1_';
  var ZERO_ID = /^[0-]+5000-0+$/;

  function readConfig(root) {
    try {
      return JSON.parse(root.querySelector('[data-delivery-promise-config]').textContent);
    } catch (e) {
      return null;
    }
  }

  function previewArm() {
    try {
      if (!window.Shopify || !Shopify.theme || Shopify.theme.role === 'main') return '';
      var v = new URLSearchParams(window.location.search).get('dp_preview') || '';
      return v === 'treatment' || v === 'control' ? v : '';
    } catch (e) {
      return '';
    }
  }

  function clientId() {
    try {
      var t = window.ShopifyAnalytics.lib.user().traits().uniqToken;
      return t && !ZERO_ID.test(String(t)) ? String(t) : '';       // all-zero id = no analytics consent
    } catch (e) {
      return '';
    }
  }

  // The analytics library loads after the theme; wait up to 2 s (as rec-row.js).
  function waitClientId() {
    return new Promise(function (resolve) {
      var tries = 0;
      (function poll() {
        var ready = window.ShopifyAnalytics && ShopifyAnalytics.lib && ShopifyAnalytics.lib.user;
        if (ready || tries++ >= 20) return resolve(clientId());
        setTimeout(poll, 100);
      })();
    });
  }

  // 0–99, or -1 when it cannot be computed (no crypto.subtle outside https).
  function bucket(salt, id) {
    if (!window.crypto || !crypto.subtle || !window.TextEncoder) return Promise.resolve(-1);
    return crypto.subtle.digest('SHA-256', new TextEncoder().encode(salt + ':' + id)).then(function (buf) {
      var b = new Uint8Array(buf), r = 0;
      for (var i = 0; i < 8; i++) r = (r * 256 + b[i]) % 100;       // = big-endian 64-bit value mod 100
      return r;
    }, function () { return -1; });
  }

  function arm(cfg) {
    var forced = previewArm();
    if (forced) return Promise.resolve(forced);
    if (!cfg.enabled) return Promise.resolve('off');
    var pct = Number(cfg.treatmentPct) || 0;
    if (pct >= 100) return Promise.resolve('treatment');
    if (pct <= 0) return Promise.resolve('control');
    return waitClientId().then(function (id) {
      if (!id) return 'unmeasured';
      return bucket(cfg.salt || 'pdp-delivery:v1', id).then(function (b) {
        return b < 0 ? 'unmeasured' : (b < pct ? 'treatment' : 'control');
      });
    });
  }

  function cached(country) {
    try {
      var c = JSON.parse(sessionStorage.getItem(CACHE_KEY + country) || 'null');
      if (c && c.slots && c.slots.length && Date.parse(c.slots[0].order_before) > Date.now() + (c.offset || 0)) return c;
    } catch (e) { /* private mode */ }
    return null;
  }

  function load(cfg) {
    var hit = cached(cfg.country);
    if (hit) return Promise.resolve(hit);
    if (!cfg.endpoint || !window.fetch) return Promise.resolve(null);
    var ctrl = window.AbortController ? new AbortController() : null;
    var timer = setTimeout(function () { if (ctrl) ctrl.abort(); }, 3000);
    var sep = cfg.endpoint.indexOf('?') >= 0 ? '&' : '?';
    return fetch(cfg.endpoint + sep + 'country=' + encodeURIComponent(cfg.country), {
      credentials: 'omit', signal: ctrl ? ctrl.signal : undefined, headers: { Accept: 'application/json' }
    }).then(function (res) {
      return res.ok ? res.json() : null;
    }).then(function (body) {
      clearTimeout(timer);
      if (!body || !body.slots || !body.slots.length) return null;
      var server = Date.parse(body.now);
      var data = { slots: body.slots, offset: isNaN(server) ? 0 : server - Date.now() };
      try { sessionStorage.setItem(CACHE_KEY + cfg.country, JSON.stringify(data)); } catch (e) { /* ignore */ }
      return data;
    }).catch(function () {
      clearTimeout(timer);
      return null;
    });
  }

  function fill(template, values) {
    return String(template || '').replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, k) {
      return values[k] !== undefined ? values[k] : '';
    });
  }

  function part(parts, type) {
    for (var i = 0; i < parts.length; i++) if (parts[i].type === type) return parts[i].value.replace(/\.$/, '');
    return '';
  }

  // «2026-11-03» ⇒ «Tue 3 Nov» per the language's pattern (products.product.delivery_promise_date).
  function formatDate(iso, cfg) {
    var d = new Date(iso + 'T12:00:00Z');
    var lang = String(cfg.locale || 'en').replace('_', '-');
    if (lang === 'no') lang = 'nb';
    var opts = { timeZone: 'Europe/Stockholm' };
    try {
      var parts = new Intl.DateTimeFormat(lang, Object.assign({ weekday: 'short', day: 'numeric', month: 'short' }, opts))
        .formatToParts(d);
      return fill(cfg.t.date, {
        weekday: part(parts, 'weekday'), day: part(parts, 'day'), month: part(parts, 'month'),
        month_num: String(d.getUTCMonth() + 1)
      }).replace(/ /g, '\u00a0');                   // «tis 3 nov» never breaks
    } catch (e) {
      return iso;
    }
  }

  function formatLeft(ms, cfg) {
    var mins = Math.max(1, Math.ceil(ms / 60000));
    var h = Math.floor(mins / 60), m = mins % 60;
    return h > 0 ? fill(cfg.t.hm, { h: h, m: m }) : fill(cfg.t.m, { m: m });
  }

  function text(data, cfg) {
    var now = Date.now() + (data.offset || 0);
    for (var i = 0; i < data.slots.length; i++) {
      var s = data.slots[i], left = Date.parse(s.order_before) - now;
      if (!(left > 0)) continue;
      var date = formatDate(cfg.dateMode === 'min' ? s.min : s.max, cfg);
      return left < 24 * 3600000 ? fill(cfg.t.within, { time: formatLeft(left, cfg), date: date })
                                 : fill(cfg.t.now, { date: date });
    }
    return '';
  }

  function publish(name, payload) {
    try {
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, payload);
    } catch (e) { /* the pixel does not forward it yet – the arm is recomputed from the clientId */ }
  }

  function init(root) {
    if (root.dataset.dpInit) return;
    root.dataset.dpInit = '1';
    var cfg = readConfig(root);
    if (!cfg || !cfg.t) return;
    var variant = String(cfg.variant || '');
    var out = root.querySelector('[data-delivery-promise-text]');

    arm(cfg).then(function (a) {
      if (a !== 'treatment' && a !== 'control') return;
      publish('timi:pdp_delivery_arm', { arm: a, country: cfg.country });
      if (a !== 'treatment' || !Object.keys(cfg.inStock || {}).length) return;   // nothing in stock ⇒ no call
      return load(cfg).then(function (data) {
        if (!data) return;
        function render() {
          var line = cfg.inStock && cfg.inStock[variant] ? text(data, cfg) : '';
          out.textContent = line;
          root.hidden = !line;
        }
        render();
        setInterval(render, 30000);
        document.addEventListener('variant:change', function (e) {
          var v = e && e.detail && e.detail.variant;
          variant = v ? String(v.id) : '';
          render();
        });
      });
    });
  }

  function start() {
    var nodes = document.querySelectorAll('[data-delivery-promise]');
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
